import { compareDesc, parseISO } from 'date-fns';
import Maybe from 'true-myth/maybe';
import { z } from 'zod';

import { idSchema } from '~/shared/data-contract/id';
import { personSchema } from '~/shared/data-contract/person';

import { positionDetailsSharedSchema } from '../../position-management/position-details';
import type { Activity } from './activity';
import { activitySchema } from './activity';
import { focusSkillSchema } from './focus-skill';

export const getAlignmentRecordPathParamsSchema = z.object({
    id: idSchema,
    userId: idSchema,
});

export type GetAlignmentRecordPathParams = z.infer<typeof getAlignmentRecordPathParamsSchema>;

const alignmentGoalSchema = z.object({
    alignmentGoalType: z.enum(['operational', 'career']),
    goalText: z.string().optional(),
});

const alignmentConfirmationSchema = z.object({
    alignmentConfirmationType: z.enum(['position', 'goals', 'focus-skills']),
    alignmentConfirmationUser: z.enum(['employee', 'manager']),
    confirmationDate: z.iso.datetime(),
});

export const alignmentRecordSchema = z.object({
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    status: z.enum(['active', 'archived']),
    updatedAt: z.iso.datetime(),
});

export type AlignmentRecord = z.infer<typeof alignmentRecordSchema>;

const sortAlignmentRecordsByUpdatedAt = (records: readonly AlignmentRecord[]): readonly AlignmentRecord[] => {
    return [...records].sort((a, b) => compareDesc(parseISO(a.updatedAt), parseISO(b.updatedAt)));
};

const sortActivitiesByActivityDate = (activities: readonly Activity[]): readonly Activity[] => {
    return [...activities].sort((a, b) => {
        const dateA = Maybe.of(a.activityDate).mapOr(parseISO(a.createdAt), parseISO);
        const dateB = Maybe.of(b.activityDate).mapOr(parseISO(b.createdAt), parseISO);

        return compareDesc(dateA, dateB);
    });
};

export const alignmentRecordsSchema = alignmentRecordSchema.array().transform(sortAlignmentRecordsByUpdatedAt);

export type AlignmentRecords = z.infer<typeof alignmentRecordsSchema>;

export const alignmentRecordDetailsSchema = alignmentRecordSchema.extend({
    activities: activitySchema.array().transform(sortActivitiesByActivityDate),
    confirmations: alignmentConfirmationSchema.array(),
    employee: personSchema,
    focusSkills: focusSkillSchema.array(),
    goals: alignmentGoalSchema.array(),
    manager: personSchema.optional(),
    position: positionDetailsSharedSchema.optional(),
});

export type AlignmentRecordDetails = z.infer<typeof alignmentRecordDetailsSchema>;
